import { EmployeeModelType } from '@teinor/erp/types/company/userInner/employee'
import { ComponentWithPermissions } from '../../../../../../../baseComponents/CreateRoutering/types'
import useTwinTranslation from '../../../../../../../utils/hooks/useTwinTranslation'
import { FormRender } from '../../../../../../../forms/FormRenderer/types'
import TwinComment, { CreateTwinComment } from '../../../../../../../baseComponents/TwinComment'
import TwinTrans from '../../../../../../../baseComponents/TwinTrans'
import PermissionChecker from '../../../../../../../baseComponents/PermissionChecker'
import TwinForm from '../../../../../../../forms/TwinForm'
import FormRenderer from '../../../../../../../forms/FormRenderer'
import { ButtonOrLoader } from '../../../../../../../baseComponents/Button'
import useIsLoading from '../../../../../../../utils/hooks/useIsLoading'
import { useParams } from 'react-router'
import { useCallback, useEffect, useState } from 'react'
import { twinFetchPostJSON } from '../../../../../../../utils/globals/data'
import { EmployeeNoteModelType } from '@teinor/erp/types/company/userInner/employee/employeeNote'
import './employeeNotes.sass'

type EmployeeNotesProps = ComponentWithPermissions & EmployeeNotesLogicProps & {
    basicData: EmployeeModelType | null
}

const EmployeeNotes: React.FC<EmployeeNotesProps> = ({ basicData, userPermissions, ...logic }) => {
    const { t } = useTwinTranslation()
    const { myOnSubmit, endLoading, startLoading, loading, employeeNotesComments, EmployeeId, getEmployeeNotesComments, deleteEmployeeNote, updateEmployeeNote } = useEmployeeNotesLogic({ ...logic })
    const fields: FormRender<EmployeeModelType> = [
        {
            cols: 1,
            title: t('notesEmployee', 'Notas del Empleado'),
            elements: [
                {
                    name: 'notes',
                    component: 'TwinEditor',
                    defaultValue: basicData?.notes || '',
                    className: 'h-300'
                },
                {
                    name: 'id',
                    component: 'InputHidden',
                    value: basicData?.id
                }
            ]
        }
    ]
    const renderThis: JSX.Element[] = []
    if (employeeNotesComments) {
        for (const key in employeeNotesComments) {
            const comment = employeeNotesComments[key]
            const parseDate = new Date(comment?.createdAt).toLocaleString()
            renderThis.push(<TwinComment key={key} comment={comment.message || ''} posArr={parseInt(key)} fieldComment='message' subtitle={parseDate} photo={comment?.EmployeeTrigger?.profile_image} {...comment} EmployeeId={comment.EmployeeTriggerId} downloadFileURL='/api/app/employee/employeeNote/getEmployeeNoteInstanceFile' deleteComment={deleteEmployeeNote} updateComment={updateEmployeeNote} title={comment?.EmployeeTrigger?.fullname_short || ''} />)
        }
    }
    return (
        <div className='grid grid-cols-2 gap-30 flex-auto employee_notes_box'>
            <div className='flex flex-col'>
                <div className='regular18 mb-17'>
                    <TwinTrans transKey='employeeComments'>Comentarios del empleado</TwinTrans>
                </div>
                <div className='flex flex-col gap-20 overflow-auto flex-auto pr-5 h-1 employee_comments'>{renderThis}</div>
                <PermissionChecker userPermissions={userPermissions} permission={'update'}>
                    <CreateTwinComment createURL={'/api/app/employee/employeeNote/createEmployeeNote'} imagePath={['employee', String(EmployeeId), 'employeeNotes']} idParent={EmployeeId || ''} fieldIdParent='EmployeeId' myOnSubmit={getEmployeeNotesComments} fieldName='message' />
                </ PermissionChecker>
            </div>
            <TwinForm action='/api/app/employee/updateEmployee' onSubmit={myOnSubmit} beforeSubmitHandler={startLoading} onError={endLoading} className=''>
                <FormRenderer sections={fields} antiUnsaveTrigger={true} />
                <PermissionChecker userPermissions={userPermissions} permission='update'>
                    <div className='flex justify-end items-center mt-40'>
                        <ButtonOrLoader textButton={t('save', 'Guardar')} buttonIsDisabled={loading} />
                    </div>
                </PermissionChecker>
            </TwinForm>
        </div>
    )
}
interface EmployeeNotesLogicProps {
    getData: () => Promise<void>
}

const useEmployeeNotesLogic = ({ getData }: EmployeeNotesLogicProps) => {
    const [employeeNotesComments, setEmployeeNotesComments] = useState<EmployeeNoteModelType[] | null>(null)
    const { startLoading, endLoading, loading } = useIsLoading()
    const { id } = useParams()

    const myOnSubmit = useCallback(() => {
        endLoading()
        getData()
    }, [endLoading, getData])

    const getEmployeeNotesComments = useCallback(async () => {
        const result: EmployeeNoteModelType[] = await twinFetchPostJSON('/api/app/employee/employeeNote/getAllEmployeeNotes', { where: { 'EmployeeId': id }, order: [['id', 'desc']] })
        setEmployeeNotesComments(result)
    }, [id])

    const updateEmployeeNote = useCallback(async (id: number, message: string, posArr: number) => {
        const result = await twinFetchPostJSON('/api/app/employee/employeeNote/updateEmployeeNote', { id, message })
        if (result) {
            setEmployeeNotesComments((old) => {
                const copyOld = JSON.parse(JSON.stringify(old))
                copyOld[posArr].message = message
                return [...copyOld]
            })
        }
    }, [setEmployeeNotesComments])

    const deleteEmployeeNote = useCallback(async (id: number, posArr: number) => {
        const result = await twinFetchPostJSON('/api/app/employee/employeeNote/deleteEmployeeNote', { id })
        if (result) {
            setEmployeeNotesComments((old) => {
                const copyOld = JSON.parse(JSON.stringify(old))
                copyOld.splice(posArr, 1)
                return [...copyOld]
            })
        }
    }, [setEmployeeNotesComments])

    useEffect(() => {
        getEmployeeNotesComments()
    }, [getEmployeeNotesComments])

    return {
        startLoading, endLoading, loading, myOnSubmit, employeeNotesComments, EmployeeId: id, getEmployeeNotesComments, updateEmployeeNote, deleteEmployeeNote }
}

export default EmployeeNotes